/**
 * ActivationCtrl controller
 * Subscriber's activation page
 */
import {
  INAPP_PAYMENT_STATUSES,
  INAPP_PAYMENT_ORIGINAL_STATUSES,
  PAYMENT_SYSTEM_TYPES,
  MONETIZATION_TYPES,
  PURCHASE_STATUSES,
  SOURCE_TYPES,
  PRODUCT_TYPES, EXTERNAL_PAYMENT_SYSTEM_TYPES, CATEGORY_TYPES,
  PARTNER_PRODUCT_STATUSES, PRODUCT_STATUSES
} from 'app/core/constants'

const module = require('../print/paymentInfo.tpl.js')

/** GoCardless void/refund window (UI-3.2): 7 days from charge; not coupled to PAYMENT_VOID_HOURS used for other gateways. */
const GOCARDLESS_VOID_REFUND_WINDOW_HOURS = 168

/**
 * Build a { TYPE: 'Label' } map for the Payment History "Payment Service" filter from the
 * Odin-filtered activation extra payload (vm.extra.paymentSystems). Labels come from
 * PAYMENT_SYSTEM_TYPES with a fallback to the raw enum key. Membership is presence in
 * extra.paymentSystems only; Odin omit/include controls whether Gr4vy appears. Do not
 * skip entries based on enabled===false (MW-12981).
 *
 * NOTE: avoids `[...new Set(arr)]` — under this repo's Jest/Babel the Set spread can transpile
 * incorrectly; dedup via a plain object instead.
 *
 * @param {Array} [paymentSystems] - extra.paymentSystems (may be null/undefined/empty)
 * @return {Object} map of unique paymentSystemType → display label
 */
function buildPaymentSystemTypesFromExtra (paymentSystems) {
  if (!Array.isArray(paymentSystems)) {
    return {}
  }
  return paymentSystems.reduce((acc, ps) => {
    if (!ps) {
      return acc
    }
    const type = ps.paymentSystemType
    if (type && !Object.prototype.hasOwnProperty.call(acc, type)) {
      acc[type] = PAYMENT_SYSTEM_TYPES[type] || type
    }
    return acc
  }, {})
}

// controller mixins
ActivationCtrl.mixes = [
  'onOffCtrlMixin'
]

/**
 * ActivationCtrl controller
 *
 * @class ActivationCtrl
 * @memberof mwApp/subscribers
 * @see mwApp/subscribers
 * @mixes mwApp/common.mixin:onOffCtrlMixin
 * @mixes mwApp/common.mixin:tabsCtrlMixin
 *
 * @ngdoc controller
 * @name mwApp/subscribers.controller:ActivationCtrl
 */
/* @ngInject */
export default function ActivationCtrl (
  $http,
  $location,
  $rootScope,
  $sce,
  $scope,
  $timeout,
  $uibModal,
  $window,
  NetworkPaymentSystem,
  PaymentSystem,
  Subscriber,
  alertLiteSvc,
  authSvc,
  extra,
  scope,
  storageSvc,
  subscr
) {
  var vm = this
  vm.mixes = [['tabsCtrlMixin', 'subscriptions', 'cards', 'payment-history', 'subscribers-library', 'partner-products']]

  /**
   * Subscriber
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @type {models.Subscriber}
   */
  vm.subscr = subscr

  /**
   * Extra
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @type {Object}
   */
  vm.extra = extra

  vm.inApp_payment_statuses = angular.copy(INAPP_PAYMENT_STATUSES)
  vm.product_statuses = angular.copy(PRODUCT_STATUSES)
  vm.inApp_payment_statuses.REFUNDED = 'Refunded'
  vm.inApp_payment_original_statuses = INAPP_PAYMENT_ORIGINAL_STATUSES
  vm.monetization_types = MONETIZATION_TYPES
  vm.purchase_statuses = PURCHASE_STATUSES
  vm.partner_product_statuses = PARTNER_PRODUCT_STATUSES
  vm.source_types = SOURCE_TYPES
  authSvc.hasAccess('voidOrRefundSubscriptionPayment')

  /**
   * External Payment System types
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @name vm.external_payment_system_types
   * @type {Object}
   */
  vm.external_payment_system_types = EXTERNAL_PAYMENT_SYSTEM_TYPES

  /**
   * Product Types
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @name vm.product_types
   * @type {Object}
   */
  vm.product_types = PRODUCT_TYPES

  /**
   * Category Types
   *
   * @memberof mwApp/subscribers.controller:ActivationStatementsCtrl
   * @name vm.category_types
   * @type {Object}
   */
  vm.category_types = CATEGORY_TYPES

  /**
   * Payment system types (full constant) — used for ROW LABELS in the Payment History table,
   * so historical purchases whose type is no longer available for new payments still render a label.
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @name vm.payment_system_types
   * @type {Object}
   */
  vm.payment_system_types = PAYMENT_SYSTEM_TYPES

  /**
   * Payment system types for the Payment History "Payment Service" FILTER — derived from the
   * Odin-filtered activation extra (vm.extra.paymentSystems), not the hardcoded constant.
   * Membership is presence-only in extra.paymentSystems; Odin omit/include controls which types
   * appear (e.g. Gr4vy). enabled===false does not exclude entries (MW-12981).
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @name vm.payment_system_types_filter
   * @type {Object}
   */
  vm.payment_system_types_filter = {}

  /**
   * Rebuild the Payment History filter map from the current vm.extra.paymentSystems.
   * Call after every vm.extra reassignment so the filter stays in sync with the activation extra.
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @function
   */
  vm.refreshPaymentSystemTypesFilter = function () {
    vm.payment_system_types_filter = buildPaymentSystemTypesFromExtra(vm.extra && vm.extra.paymentSystems)
  }

  vm.refreshPaymentSystemTypesFilter()

  vm.payment_methods_labels = { GOOGLEPAY: 'Google Pay', APPLEPAY: 'Apple Pay', CREDIT_CARD: 'Card' }

  /**
   * Main Subscription
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @type {models.Subscription}
   */
  vm.mainSubscription = subscr._currentMainSubscription()

  /**
   * Currencies
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @type {models.Currency}
   */
  if (storageSvc.local.get('currencies')) {
    vm.currencies = JSON.parse(storageSvc.local.get('currencies'))
  } else {
    vm.currencies = []
  }

  /**
   * Approve payment statement
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   */
  vm.approve = function () {
    subscr.currentPaymentStatement._approve()
      .then(vm.location.reload)
  }

  /**
   * Get Show Sets from backend
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @param {Object} tableState - smart table state object
   */
  vm.getInappPurchases = function (tableState) {
    if (vm.currentPurchaseNumber && vm.currentPurchaseNumber !== tableState.search.predicateObject.purchaseNumber) {
      vm.currentPurchaseNumber = null
      $location.search('paymentNumber', null)
    }
    if ($location.search().paymentNumber) {
      tableState.search.predicateObject = { ...tableState.search.predicateObject, purchaseNumber: $location.search().paymentNumber }
      vm.currentPurchaseNumber = $location.search().paymentNumber
    }
    // set default sort by createdTime
    if (!tableState.sort.predicate) {
      tableState.sort.predicate = 'createdTime'
      tableState.sort.reverse = true
    }
    // set default filter paymentStatus
    if (!tableState.search.predicateObject) {
      tableState.search.predicateObject = {
        paymentStatuses: ['PAID', 'REFUNDED', 'VOID', 'PAYMENT_FAILED', 'REVERSED', 'PENDING_PAYMENT']
      }
    }

    Subscriber.getInappPurchases(tableState, vm.subscr.id)
      .then(
        data => {
          vm.inappPurchases = data.content

          /** Whether purchase line items qualify for void/refund window rules (runs before reorder in second pass). */
          function qualifiesForVoidByPurchaseRules (purchaseItems) {
            return purchaseItems.reduce((acc, item) => {
              if (['Subscription', 'Custom_Subscription', 'Add_Device'].includes(item.productType) && ['ACTIVE', 'UPCOMING', 'PENDING_TOA', 'PENDING', 'ON_HOLD'].includes(item.subscriptionStatus)) {
                return acc && true
              }
              if (item.productType === 'Trial_Subscription') {
                return acc && false
              }
              if (!['Subscription', 'Custom_Subscription', 'Trial_Subscription', 'Add_Device'].includes(item.productType)) {
                return acc && true
              }
              return acc && false
            }, true)
          }

          vm.inappPurchases.forEach((purchase) => {
            const voidByPurchases = qualifiesForVoidByPurchaseRules(purchase.purchaseItems)
            if (purchase.status !== 'PAID' || !voidByPurchases) {
              return
            }
            if (['GR4VY_GATEWAY', 'AIRWALLEX'].includes(purchase.paymentSystemType)) {
              return
            }

            if (purchase.paymentSystemType === 'GOCARDLESS') {
              const withinGcWindow = purchase.processedTime
                .clone()
                .add(GOCARDLESS_VOID_REFUND_WINDOW_HOURS, 'hours')
                .isAfter()
              if (withinGcWindow) {
                purchase.__voidable = true
              } else {
                purchase.__refundPeriodExpired = true
              }
              return
            }

            if (scope.settings.PAYMENT_VOID_HOURS !== 0) {
              const paymentVoidHours = Number(scope.settings.PAYMENT_VOID_HOURS)
              const withinVoidWindow = purchase.processedTime
                .clone()
                .add(paymentVoidHours, 'hours')
                .isAfter()
              if (withinVoidWindow) {
                purchase.__voidable = true
              }
            }
          })

          vm.inappPurchases.forEach((purchase) => {
            const subscriptionsArr = []
            const contentAddonsArr = []
            const addonsArr = []
            const contentItemsArr = []
            const accessoryArr = []
            const refundByPurchases = purchase.purchaseItems.reduce((acc, item) => {
              if (['Subscription', 'Custom_Subscription', 'Trial_Subscription', 'Add_Device'].includes(item.productType)) {
                subscriptionsArr.push(item)
              } else if (item.productType === 'Content_addOn') {
                contentAddonsArr.push(item)
              } else if (item.productType === 'Content_Item') {
                contentItemsArr.push(item)
              } else if (item.productType === 'Accessory') {
                accessoryArr.push(item)
              } else {
                addonsArr.push(item)
              }
              if (['Subscription', 'Custom_Subscription', 'Add_Device'].includes(item.productType) && ['ACTIVE', 'UPCOMING', 'PENDING_TOA', 'PENDING', 'ON_HOLD'].includes(item.subscriptionStatus)) {
                return acc && true
              }
              if (item.productType === 'Trial_Subscription') {
                return acc && false
              }
              if (!['Subscription', 'Custom_Subscription', 'Trial_Subscription', 'Add_Device'].includes(item.productType)) {
                return acc && true
              }
              return acc && false
            }, true)
            if (purchase.status === 'PAID' && ['GR4VY_GATEWAY', 'AIRWALLEX'].includes(purchase.paymentSystemType) && refundByPurchases) {
              purchase.__refundable = true
            }
            purchase.purchaseItems = subscriptionsArr.concat(accessoryArr, contentAddonsArr, contentItemsArr, addonsArr)
          })
          if ($location.search().paymentNumber && vm.inappPurchases.length) {
            vm.inappPurchases[0].isOpen = true
          }
          if (vm.adjustedPurchase) {
            vm.inappPurchases.forEach(p => {
              if (p.id === vm.adjustedPurchase.id) {
                p.isOpen = true
              }
            })
          }
          if (tableState.search.predicateObject.subscriptionStatus) {
            vm.inappPurchases.forEach(p => {
              p.isOpen = true
            })
          }
        }
      )
  }

  /**
   * Get Show Sets from backend
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @param {Object} tableState - smart table state object
   */
  vm.getPartnerProducts = function (tableState) {
    Subscriber.getPartnerProducts(tableState, vm.subscr.id)
      .then(
        data => {
          vm.partnerProducts = data.content
        }
      )
  }

  /**
   * Return currency by code
   *
   * @memberof mwApp/subscriber.controller:ActivationCtrl
   * @function
   * @return  {model.Currency}
   * @param code
   */
  vm.getCurrencyByCode = function (code) {
    return vm.currencies.find(c => c.name === code)
  }

  vm.formatDescription = function (purchase) {
    return 'Buy ' + purchase.category.split('_').map(cat => {
      if (cat === 'TV') return cat
      return cat.slice(0, 1) + cat.slice(1).toLowerCase()
    }).join(' ') + ' "' + purchase.name + '"'
  }

  // apply set tab from hash
  scope.applyTabChange = () => $timeout(() => {
    vm.setTab(true) // 'true' means 'set tab from hash'
    $scope.$apply()
  }, 0)

  /** Add/edit internal payment comment: requires view + create subscription payment. */
  var INTERNAL_PAYMENT_COMMENT_EDIT_ACCESS = 'viewSubscriptionPayment createSubscriptionPayment &'

  vm.canEditInternalPaymentComment = function () {
    return authSvc.hasAccess(INTERNAL_PAYMENT_COMMENT_EDIT_ACCESS)
  }

  function openPaymentStatementCommentModal (purchase, allowEdit) {
    if (vm.subscr.deleted || !authSvc.hasAccess(INTERNAL_PAYMENT_COMMENT_EDIT_ACCESS)) {
      return
    }
    const initialComment = purchase.comment
    $uibModal.open({
      templateUrl: 'components/subscribers/activation/views/addPaymentStatementComment.html',
      controller: 'AddPaymentStatementCommentCtrl as vm',
      animation: false,
      size: 'lg',
      resolve: {
        statement: () => purchase,
        allowEdit: () => allowEdit
      }
    }).result
      .catch(() => { purchase.comment = initialComment })
  }

  /**
   * Add comment to payment purchase
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @function
   * @param  {models.PaymentStatement} purchase
   */
  vm.addComment = function (purchase) {
    openPaymentStatementCommentModal(purchase, true)
  }

  /**
   * Open modal to edit an existing payment purchase comment
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @function
   * @param  {models.PaymentStatement} purchase
   */
  vm.editComment = function (purchase) {
    openPaymentStatementCommentModal(purchase, true)
  }

  /**
   * Load internal comment history for a purchase (API) and show it in a modal
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @param {Object} $event
   * @param {models.PaymentStatement} purchase
   */
  vm.showPurchaseCommentHistories = function ($event, purchase) {
    vm.stopEvent($event)
    if (vm.subscr.deleted || !authSvc.hasAccess('viewSubscriptionPayment')) {
      return
    }
    $uibModal.open({
      templateUrl: 'components/subscribers/activation/modals/purchaseCommentHistoriesModal.html',
      controller: 'PurchaseCommentHistoriesModalCtrl as vm',
      animation: false,
      size: 'lg',
      resolve: {
        purchase: function () {
          return purchase
        },
        purchaseNumber: function () {
          return purchase.purchaseNumber
        }
      }
    })
  }

  vm.adjustSubscription = function (subscription, purchase) {
    vm.adjustedPurchase = angular.copy(purchase)
    const initialSubscription = angular.copy(subscription)
    $uibModal.open({
      templateUrl: 'components/subscribers/activation/views/adjustSubscription.html',
      controller: 'AdjustSubscriptionCtrl as vm',
      animation: false,
      size: 'lg',
      resolve: {
        subscription: () => subscription,
        subscriber: () => subscr,
        maxLength: () => scope.settings.ACTIVATION_LENGTH_LIMIT,
        addOns: () => vm.extra.activeRentContentAddOns
      }
    }).result
      .then(() => {
        $scope.$broadcast('refreshTable', { id: 'purchasesTable' })
        $scope.$broadcast('refreshTable', { id: 'statementsTable' })
        Subscriber.loadExtra({ id: vm.subscr.id || 0, url: '?activation' }).then(res => {
          vm.extra = res
          vm.refreshPaymentSystemTypesFilter()
        })
      })
      .catch(() => { subscription = angular.copy(initialSubscription) })
  }

  vm.showTooltip = function (purchase) {
    return !!purchase.purchaseItems.find(i => i.paidDevices > 0)
  }

  /**
   * Void payment statement
   *
   * @memberof mwApp/subscribers.controller:ActivationStatementsCtrl
   * @function
   * @param  {models.PaymentStatement} statement
   */
  vm.void = function (statement) {
    statement.paymentSystemType === 'GR4VY_GATEWAY' ? statement.__refundable = false : statement.__voidable = false
    statement._void()
      .then(vm.location.reload)
      .catch((err) => {
        let ids
        if (err.data.split('[').length > 1) {
          ids = err.data.split('[')[1].substring(0, err.data.split('[')[1].length - 1).split(',')
          ids = ids.map((id) => id.trim())
        } else {
          alertLiteSvc.alertError(err.data)
        }
        if (ids) {
          $uibModal.open({
            templateUrl: 'components/subscribers/activation/views/showConflicts.html',
            controller: 'ShowConflictsCtrl as vm',
            animation: false,
            size: 'lg',
            resolve: {
              ids: () => ids,
              type: () => statement.paymentSystemType === 'GR4VY_GATEWAY' ? 'refund' : 'void',
              serverError: () => err.status >= 500
            }
          }).result
            .then((res) => {
              $location.hash('payment-history')
              $location.search('paymentNumber', res)
            })
            .catch()
        }

        statement.paymentSystemType === 'GR4VY_GATEWAY' ? statement.__refundable = true : statement.__voidable = true
      })
  }

  vm.downloadReceipt = function (subscriberId, purchase) {
    $http.get(`/nora/api/subscribers/${subscriberId}/purchases/${purchase.id}/receipt`).then(res => {
      module.prepareData({ ...res.data.payload, transactionFeeEnabled: vm.extra.transactionFeeEnabled })
    })
  }

  if (authSvc.hasEnabledFlag('FEATURE_SUBSCRIBER_PORTAL_REDIRECT_ENABLED')) {
    subscr._getRedirectLinkStatus()
      .then(res => {
        vm.portalStatus = res
        scope.portalStatus = res
      })
  }

  vm.redirectToSC = function (fromPaymentDetails = false) {
    const params = {}
    if (fromPaymentDetails) {
      params.redirectTo = 'WALLETS'
    }

    subscr._getRedirectLink(params)
      .then(res => {
        if (fromPaymentDetails) {
          scope.portalRedirectClicked = true
        }
        $window.open(res, '_blank')
      })
  }

  scope.redirectToSC = vm.redirectToSC
  scope.portalStatus = vm.portalStatus

  /**
   * Return object keys length
   *
   * @memberof mwApp/subscriber.controller:ActivationCtrl
   * @function
   * @param obj
   * @return number
   */
  vm.objectKeysLength = function (obj) {
    return Object.keys(obj).length
  }

  vm.showDashFrom = function (purchase, item) {
    if (!['GOCARDLESS'].includes(item.paymentMethod) && ['PAYMENT_FAILED', 'PENDING_PAYMENT', 'CANCELLED'].includes(purchase.status)) {
      return true
    }
    return ['Custom_Charge', 'Add_On', 'Misc'].includes(item.productType)
  }
  vm.showDashUntil = function (purchase, item) {
    if (!['GOCARDLESS'].includes(item.paymentMethod) && ['PAYMENT_FAILED', 'PENDING_PAYMENT', 'CANCELLED'].includes(purchase.status)) {
      return true
    }
    if (['Custom_Charge', 'Add_On', 'Misc'].includes(item.productType)) {
      return true
    }
    return ['Add_On', 'Content_Item'].includes(item.productType) && ['PURCHASE', 'BUY'].includes(item.monetizationType)
  }
  vm.showDashLength = function (item) {
    if (['Add_On', 'Content_Item'].includes(item.productType) && ['PURCHASE', 'BUY'].includes(item.monetizationType)) {
      return true
    }
    return ['Custom_Charge', 'Add_On', 'Misc'].includes(item.productType)
  }

  vm.onOffProduct = function (product) {
    if (['PENDING', 'FAILED'].includes(product.status)) {
      Subscriber.activateProduct(vm.subscr.id, product.id).then(() => {
        $scope.$broadcast('refreshTable')
      })
    }
    if (product.status === 'ACTIVE') {
      Subscriber.voidProduct(vm.subscr.id, product.id).then(() => {
        $scope.$broadcast('refreshTable', { id: 'partnerProductsTable' })
      })
    }
  }

  vm.onOffSubscr = function () {
    vm.onOff(vm.subscr).then(() => {
      if (authSvc.hasEnabledFlag('FEATURE_SUBSCRIBER_PORTAL_REDIRECT_ENABLED')) {
        subscr._getRedirectLinkStatus()
          .then(res => {
            vm.portalStatus = res
            scope.portalStatus = res
          })
      }
    })
  }

  vm.hasFlag = function (flag) {
    return authSvc.hasEnabledFlag(flag)
  }
  scope.hasFlag = vm.hasFlag

  /**
   * Check if trial is still active
   *
   * @memberof mwApp/subscriber.controller:ActivationCtrl
   * @function
   * @return boolean
   */
  vm.isTrialActive = function () {
    if (!vm.extra.trial || !vm.extra.trial.activeUntil) {
      return false
    }
    const trialDate = new Date(vm.extra.trial.activeUntil)
    const now = new Date()
    return trialDate > now
  }

  vm.loadDiscountIcon = function () {
    return $sce.trustAsHtml(`<svg width="11" height="14" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M13.082 3.30469C13.5039 3.72656 13.75 4.28906 13.75 4.88672V15.75C13.75 17.0156 12.7305 18 11.5 18H2.5C1.23438 18 0.25 17.0156 0.25 15.75V2.25C0.25 1.01953 1.23438 0 2.5 0H8.86328C9.46094 0 10.0234 0.246094 10.4453 0.667969L13.082 3.30469ZM12.0625 15.75V5.625H9.25C8.61719 5.625 8.125 5.13281 8.125 4.5V1.72266H2.5C2.18359 1.72266 1.9375 1.96875 1.9375 2.28516V15.75C1.9375 16.0664 2.18359 16.3125 2.5 16.3125H11.5C11.7812 16.3125 12.0625 16.0664 12.0625 15.75ZM8.65234 8.40234C8.96875 8.08594 9.49609 8.08594 9.8125 8.4375C10.1641 8.75391 10.1641 9.28125 9.8125 9.59766L8.16016 11.25L7 12.4453L5.34766 14.0977C4.99609 14.4492 4.46875 14.4492 4.15234 14.0977C3.80078 13.7812 3.80078 13.2539 4.15234 12.9375L5.80469 11.25L7 10.0898L8.65234 8.40234Z" fill="#333333"/>
                                      <path d="M5.56176 8.99999C5.56176 9.46944 5.1812 9.84999 4.71176 9.84999C4.24231 9.84999 3.86176 9.46944 3.86176 8.99999C3.86176 8.53055 4.24231 8.14999 4.71176 8.14999C5.1812 8.14999 5.56176 8.53055 5.56176 8.99999Z" fill="#333333"/>
                                      <path d="M10.0635 13.5018C10.0635 13.9713 9.68297 14.3518 9.21353 14.3518C8.74408 14.3518 8.36353 13.9713 8.36353 13.5018C8.36353 13.0324 8.74408 12.6518 9.21353 12.6518C9.68297 12.6518 10.0635 13.0324 10.0635 13.5018Z" fill="#333333"/>
                                    </svg>`)
  }

  vm.openDiscountModal = function (item, payment) {
    $uibModal.open({
      templateUrl: 'components/subscribers/activation/modals/discountDetailsModal.html',
      controller: 'DiscountDetailsModalCtrl as vm',
      animation: false,
      size: 'lg',
      resolve: {
        item: function () {
          return item
        },
        purchaseNumber: function () {
          return payment.purchaseNumber
        }
      }
    })
  }

  $scope.$on('$locationChangeSuccess', function () {
    $timeout(function () {
      if (vm.activeTab !== 2 && $location.search().paymentNumber) {
        delete $location.search('paymentNumber', null)
      }
    }, 500)

    if ($location.search().paymentNumber) {
      vm.setTab(true)
      $scope.$broadcast('refreshTable', { id: 'purchasesTable' })
    }
  })

  $scope.$watch('vm.subscr.currentPaymentStatement.autoPay', (n, o) => {
    if (n !== o) {
      $scope.$broadcast('refreshTable', { id: 'purchasesTable' })
      $scope.$broadcast('refreshTable', { id: 'statementsTable' })
    }
  })

  vm.autopayChanged = function () {
    vm.subscr._onOffAutopay()
      .then(
        () => {
          $scope.$broadcast('refreshTable', { id: 'purchasesTable' })
          $scope.$broadcast('refreshTable', { id: 'statementsTable' })
        }
      )
  }

  /**
   * Callback when tab is changed
   * Refreshes tables when corresponding tabs are opened
   *
   * @memberof mwApp/subscribers.controller:ActivationCtrl
   * @function
   * @param {string} tabName - name of the active tab
   * @param {number} tabIndex - index of the active tab
   */
  vm.toChangeTab = function (tabName, tabIndex) {
    // Refresh Purchase History table when Subscriptions tab (index 0) is opened
    if (tabName === 'subscriptions' && tabIndex === 0) {
      $scope.$broadcast('refreshTable', { id: 'statementsTable' })
    }
    // Refresh Payment History table when Payment History tab (index 2) is opened
    if (tabName === 'payment-history' && tabIndex === 2) {
      $scope.$broadcast('refreshTable', { id: 'purchasesTable' })
    }
  }
}
